/**
 * Administration. ADMIN only.
 *
 * Five areas behind tabs, plus the audit trail. The route is hidden from a
 * SALES user and every endpoint behind it is restricted server-side, so this
 * page never has to be the security boundary — which is what lets each panel
 * stay a plain form rather than a permissions puzzle.
 *
 * The scope is deliberately small: no Word template designer, no identity
 * platform. Templates are seeded with the deployment and toggled here;
 * authentication stays behind an interface so Entra ID SSO can replace it
 * without this screen changing.
 */
import { useEffect, useState } from 'react';
import { endpoints } from '../lib/api';
import { useApiErrorHandler, useAuth } from '../lib/auth';
import { Alert, Card, Skeleton, formatDateTime } from '../components/ui';
import { TemplatesAdmin } from './admin/TemplatesAdmin';
import { ContentAdmin } from './admin/ContentAdmin';
import { PaymentTermsAdmin } from './admin/PaymentTermsAdmin';
import { UsersAdmin } from './admin/UsersAdmin';
import { ConfigurationAdmin } from './admin/ConfigurationAdmin';

interface AuditRow {
  auditEventId: string;
  action: string;
  entityType: string;
  entityId: string;
  actorEmail: string | null;
  ip: string;
  createdAt: string;
  detail?: Record<string, unknown>;
}

const TABS = [
  { id: 'templates', label: 'Templates' },
  { id: 'content', label: 'Approved content' },
  { id: 'payment-terms', label: 'Payment terms' },
  { id: 'users', label: 'Users' },
  { id: 'configuration', label: 'Configuration' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** Admin actions worth showing on the change history, distinct from security events. */
const ADMIN_ACTIONS = new Set([
  'TEMPLATE_ACTIVATED',
  'TEMPLATE_DEACTIVATED',
  'TEMPLATE_DEFAULT_VERSION_SET',
  'APPROVED_CONTENT_CREATED',
  'APPROVED_CONTENT_UPDATED',
  'APPROVED_CONTENT_RETIRED',
  'APPROVED_CONTENT_RESTORED',
  'PAYMENT_TERM_CREATED',
  'PAYMENT_TERM_UPDATED',
  'PAYMENT_TERM_DELETED',
  'CONFIGURATION_UPDATED',
  'USER_CREATED',
  'USER_UPDATED',
]);

export function AdministrationPage() {
  const handleError = useApiErrorHandler();
  const { user } = useAuth();

  const [tab, setTab] = useState<TabId>('templates');
  const [events, setEvents] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    endpoints
      .auditEvents(60)
      .then((result) => setEvents(result.events as unknown as AuditRow[]))
      .catch((caught) => setError(handleError(caught)));
    // Re-read after a change in any panel, so the history reflects what was
    // just done without a manual refresh.
  }, [handleError, tab]);

  const onError = (message: string) => setError(message);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-ink">Administration</h1>
        <p className="mt-0.5 text-[13px] text-muted">
          Templates, approved content, payment terms, users and application settings.
        </p>
      </div>

      {error && (
        <Alert tone="error">
          {error}{' '}
          <button type="button" onClick={() => setError(null)} className="font-semibold underline">
            Dismiss
          </button>
        </Alert>
      )}

      <nav aria-label="Administration areas">
        <ul className="flex flex-wrap gap-1 border-b border-rule">
          {TABS.map((entry) => {
            const active = entry.id === tab;
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setTab(entry.id)}
                  className={[
                    '-mb-px border-b-2 px-4 py-2.5 text-[13px] font-medium',
                    active
                      ? 'border-teal text-ink'
                      : 'border-transparent text-muted hover:text-ink',
                  ].join(' ')}
                >
                  {entry.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {tab === 'templates' && <TemplatesAdmin onError={onError} />}
      {tab === 'content' && <ContentAdmin onError={onError} />}
      {tab === 'payment-terms' && <PaymentTermsAdmin onError={onError} />}
      {tab === 'users' && <UsersAdmin currentUserId={user?.userId ?? ''} onError={onError} />}
      {tab === 'configuration' && <ConfigurationAdmin onError={onError} />}

      <Card
        title="Change history"
        description="Administrative changes and security-relevant events, newest first."
      >
        {!events ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-rule text-left text-[11px] uppercase tracking-wide text-muted">
                  <th className="px-5 py-2 font-medium">When</th>
                  <th className="px-5 py-2 font-medium">Action</th>
                  <th className="px-5 py-2 font-medium">Entity</th>
                  <th className="px-5 py-2 font-medium">Actor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {events.map((event) => (
                  <tr key={event.auditEventId}>
                    <td className="px-5 py-2.5 text-muted">{formatDateTime(event.createdAt)}</td>
                    <td className="px-5 py-2.5">
                      <span
                        className={
                          ADMIN_ACTIONS.has(event.action)
                            ? 'font-medium text-ink'
                            : 'text-muted'
                        }
                      >
                        {event.action.replaceAll('_', ' ').toLowerCase()}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-muted">
                      {event.entityType.toLowerCase()} · {event.entityId}
                    </td>
                    <td className="px-5 py-2.5 text-muted">{event.actorEmail ?? 'system'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
